import type {
  KioskLayoutWidget,
  KioskRenderDocument,
  KioskTemplateVisibility,
  KioskVisibilityRule,
  KioskWidgetPosition,
} from '@cluster/types';
import type { ParseOutcome, RendererIssue } from './errors';
import { failParse, KioskRendererError, okParse } from './errors';
import { FROZEN_RENDER_GEOMETRY as GEOMETRY } from './geometry';
import { isKnownWidgetType } from './registry';

/** The four visibility collections frozen in the wire contract. */
export const VISIBILITY_COLLECTIONS = Object.freeze([
  'categories',
  'products',
  'featuredProducts',
  'discounts',
] as const);

export type VisibilityCollection = (typeof VISIBILITY_COLLECTIONS)[number];

const WIDGET_KEYS = ['widgetId', 'type', 'position', 'config', 'zIndex'] as const;
const POSITION_KEYS = ['x', 'y', 'w', 'h'] as const;
const DOCUMENT_KEYS = ['schemaVersion', 'templateId', 'version', 'layout', 'visibility'] as const;
const VISIBILITY_KEYS = ['channel', ...VISIBILITY_COLLECTIONS] as const;
const RULE_KEYS = ['allowIds', 'denyIds'] as const;

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function issue(issues: RendererIssue[], path: string, code: RendererIssue['code'], message: string): void {
  issues.push({ path, code, message });
}

function checkKnownKeys(
  issues: RendererIssue[],
  obj: Record<string, unknown>,
  allowed: readonly string[],
  path: string,
): void {
  for (const key of Object.keys(obj)) {
    if (!allowed.includes(key)) {
      issue(issues, `${path}.${key}`, 'UNKNOWN_FIELD', `Field "${key}" is not part of the frozen render contract`);
    }
  }
}

/** Mirrors the service `idSchema`: trimmed string, 1..maxIdLength characters. */
function parseId(issues: RendererIssue[], value: unknown, path: string, label: string): string | undefined {
  if (typeof value !== 'string') {
    issue(issues, path, 'INVALID_TYPE', `${label} must be a string`);
    return undefined;
  }
  const trimmed = value.trim();
  if (trimmed.length === 0) {
    issue(issues, path, 'MISSING_FIELD', `${label} must not be empty`);
    return undefined;
  }
  if (trimmed.length > GEOMETRY.maxIdLength) {
    issue(issues, path, 'OUT_OF_BOUNDS', `${label} exceeds ${GEOMETRY.maxIdLength} characters`);
    return undefined;
  }
  return trimmed;
}

function parsePosition(issues: RendererIssue[], value: unknown, path: string): KioskWidgetPosition | undefined {
  if (!isPlainObject(value)) {
    issue(issues, path, 'INVALID_TYPE', 'position must be an object with x, y, w, h');
    return undefined;
  }
  checkKnownKeys(issues, value, POSITION_KEYS, path);

  const bounds: Record<(typeof POSITION_KEYS)[number], { min: number; max: number }> = {
    x: { min: 0, max: GEOMETRY.canvas.width },
    y: { min: 0, max: GEOMETRY.canvas.height },
    w: { min: 1, max: GEOMETRY.canvas.width },
    h: { min: 1, max: GEOMETRY.canvas.height },
  };
  const parsed: Partial<KioskWidgetPosition> = {};
  for (const key of POSITION_KEYS) {
    const raw = value[key];
    const { min, max } = bounds[key];
    if (typeof raw !== 'number' || !Number.isInteger(raw)) {
      issue(issues, `${path}.${key}`, 'INVALID_TYPE', `${key} must be an integer CSS pixel value`);
      continue;
    }
    if (raw < min || raw > max) {
      issue(issues, `${path}.${key}`, 'OUT_OF_BOUNDS', `${key} must be between ${min} and ${max} inclusive`);
      continue;
    }
    parsed[key] = raw;
  }
  if (Object.keys(parsed).length !== POSITION_KEYS.length) return undefined;
  const position = parsed as KioskWidgetPosition;

  // Canvas containment per the frozen contract (mirrors service validation).
  if (position.x + position.w > GEOMETRY.canvas.width) {
    issue(issues, path, 'OUT_OF_BOUNDS', `Widget extends beyond the ${GEOMETRY.canvas.width}px canvas width`);
    return undefined;
  }
  if (position.y + position.h > GEOMETRY.canvas.height) {
    issue(issues, path, 'OUT_OF_BOUNDS', `Widget extends beyond the ${GEOMETRY.canvas.height}px canvas height`);
    return undefined;
  }
  return position;
}

function parseWidget(issues: RendererIssue[], value: unknown, path: string): KioskLayoutWidget | undefined {
  if (!isPlainObject(value)) {
    issue(issues, path, 'INVALID_TYPE', 'Layout entry must be an object');
    return undefined;
  }
  checkKnownKeys(issues, value, WIDGET_KEYS, path);

  if (!('widgetId' in value)) issue(issues, `${path}.widgetId`, 'MISSING_FIELD', 'widgetId is required');
  if (!('type' in value)) issue(issues, `${path}.type`, 'MISSING_FIELD', 'type is required');
  if (!('position' in value)) issue(issues, `${path}.position`, 'MISSING_FIELD', 'position is required');
  if (!('config' in value)) issue(issues, `${path}.config`, 'MISSING_FIELD', 'config is required');
  if (!('zIndex' in value)) issue(issues, `${path}.zIndex`, 'MISSING_FIELD', 'zIndex is required');

  const widgetId = 'widgetId' in value ? parseId(issues, value.widgetId, `${path}.widgetId`, 'widgetId') : undefined;

  let type: KioskLayoutWidget['type'] | undefined;
  if ('type' in value) {
    if (typeof value.type !== 'string') {
      issue(issues, `${path}.type`, 'INVALID_TYPE', 'Widget type must be a string');
    } else if (!isKnownWidgetType(value.type)) {
      // Unknown identifiers are an explicit typed failure — never a silent skip.
      issue(
        issues,
        `${path}.type`,
        'UNKNOWN_WIDGET_TYPE',
        `Widget type "${value.type}" is not in the frozen kiosk widget catalog`,
      );
    } else {
      type = value.type;
    }
  }

  const position = 'position' in value ? parsePosition(issues, value.position, `${path}.position`) : undefined;

  let config: Record<string, unknown> | undefined;
  if ('config' in value) {
    if (!isPlainObject(value.config)) {
      issue(issues, `${path}.config`, 'INVALID_TYPE', 'config must be a JSON object (opaque to the renderer)');
    } else {
      // Config semantics are service-validated; the renderer passes values
      // through untouched — including any server-returned display strings.
      config = value.config;
    }
  }

  let zIndex: number | undefined;
  if ('zIndex' in value) {
    const raw = value.zIndex;
    if (typeof raw !== 'number' || !Number.isInteger(raw)) {
      issue(issues, `${path}.zIndex`, 'INVALID_TYPE', 'zIndex must be an integer');
    } else if (raw < GEOMETRY.zIndex.min || raw > GEOMETRY.zIndex.max) {
      issue(
        issues,
        `${path}.zIndex`,
        'OUT_OF_BOUNDS',
        `zIndex must be between ${GEOMETRY.zIndex.min} and ${GEOMETRY.zIndex.max} inclusive`,
      );
    } else {
      zIndex = raw;
    }
  }

  if (widgetId === undefined || type === undefined || position === undefined || config === undefined || zIndex === undefined) {
    return undefined;
  }
  return { widgetId, type, position, config, zIndex };
}

function parseRule(issues: RendererIssue[], value: unknown, path: string): KioskVisibilityRule | undefined {
  if (!isPlainObject(value)) {
    issue(issues, path, 'INVALID_TYPE', 'Visibility rule must be an object with allowIds and denyIds');
    return undefined;
  }
  checkKnownKeys(issues, value, RULE_KEYS, path);

  const rules: Partial<Record<'allowIds' | 'denyIds', string[]>> = {};
  for (const key of RULE_KEYS) {
    const listPath = `${path}.${key}`;
    if (!(key in value)) {
      issue(issues, listPath, 'MISSING_FIELD', `${key} is required`);
      continue;
    }
    const raw = value[key];
    if (!Array.isArray(raw)) {
      issue(issues, listPath, 'INVALID_TYPE', `${key} must be an array of record IDs`);
      continue;
    }
    if (raw.length > GEOMETRY.maxVisibilityIdsPerRule) {
      issue(issues, listPath, 'LIST_TOO_LONG', `${key} exceeds ${GEOMETRY.maxVisibilityIdsPerRule} entries`);
      continue;
    }
    const ids: string[] = [];
    let valid = true;
    for (let i = 0; i < raw.length; i += 1) {
      const id = parseId(issues, raw[i], `${listPath}[${i}]`, `${key} entry`);
      if (id === undefined) {
        valid = false;
        continue;
      }
      if (ids.includes(id)) {
        issue(issues, `${listPath}[${i}]`, 'DUPLICATE_ID', `${key} lists duplicate record ID "${id}"`);
        valid = false;
        continue;
      }
      ids.push(id);
    }
    if (valid) rules[key] = ids;
  }

  if (!rules.allowIds || !rules.denyIds) return undefined;
  return { allowIds: rules.allowIds, denyIds: rules.denyIds };
}

function parseVisibility(issues: RendererIssue[], value: unknown): KioskTemplateVisibility | undefined {
  const path = 'visibility';
  if (!isPlainObject(value)) {
    issue(issues, path, 'INVALID_TYPE', 'visibility must be an object');
    return undefined;
  }
  checkKnownKeys(issues, value, VISIBILITY_KEYS, path);

  let channel: 'kiosk' | undefined;
  if (!('channel' in value)) {
    issue(issues, `${path}.channel`, 'MISSING_FIELD', 'channel is required');
  } else if (value.channel !== 'kiosk') {
    issue(issues, `${path}.channel`, 'INVALID_VALUE', 'channel must be the frozen literal "kiosk"');
  } else {
    channel = 'kiosk';
  }

  const rules: Partial<Record<VisibilityCollection, KioskVisibilityRule>> = {};
  for (const collection of VISIBILITY_COLLECTIONS) {
    if (!(collection in value)) {
      issue(issues, `${path}.${collection}`, 'MISSING_FIELD', `${collection} visibility rule is required`);
      continue;
    }
    const rule = parseRule(issues, value[collection], `${path}.${collection}`);
    if (rule) rules[collection] = rule;
  }

  if (
    channel === undefined ||
    !rules.categories ||
    !rules.products ||
    !rules.featuredProducts ||
    !rules.discounts
  ) {
    return undefined;
  }
  return {
    channel,
    categories: rules.categories,
    products: rules.products,
    featuredProducts: rules.featuredProducts,
    discounts: rules.discounts,
  };
}

/**
 * Parses an unknown value (the JSON body of `GET /published` or a preview
 * response) into the frozen `KioskRenderDocument`. Validation mirrors the
 * kiosks service wire bounds: strict object keys, canvas containment, zIndex
 * 0–100, max 100 widgets, unique widget IDs, trimmed ID lists, and known
 * widget identifiers only. Returns every deterministic issue found; never
 * partially accepts a document.
 */
export function parseKioskRenderDocument(value: unknown): ParseOutcome<KioskRenderDocument> {
  const issues: RendererIssue[] = [];

  if (!isPlainObject(value)) {
    issue(issues, '$', 'INVALID_TYPE', 'Render document must be a JSON object');
    return failParse(issues);
  }
  checkKnownKeys(issues, value, DOCUMENT_KEYS, '$');

  let schemaVersion: 1 | undefined;
  if (!('schemaVersion' in value)) {
    issue(issues, 'schemaVersion', 'MISSING_FIELD', 'schemaVersion is required');
  } else if (value.schemaVersion !== 1) {
    issue(
      issues,
      'schemaVersion',
      'SCHEMA_VERSION_UNSUPPORTED',
      `Renderer supports schemaVersion 1 only, received ${JSON.stringify(value.schemaVersion)}`,
    );
  } else {
    schemaVersion = 1;
  }

  let templateId: string | undefined;
  if (!('templateId' in value)) {
    issue(issues, 'templateId', 'MISSING_FIELD', 'templateId is required');
  } else {
    templateId = parseId(issues, value.templateId, 'templateId', 'templateId');
  }

  let version: number | undefined;
  if (!('version' in value)) {
    issue(issues, 'version', 'MISSING_FIELD', 'version is required');
  } else if (typeof value.version !== 'number' || !Number.isInteger(value.version) || value.version < 1) {
    issue(issues, 'version', 'INVALID_VALUE', 'version must be an integer >= 1');
  } else {
    version = value.version;
  }

  let layout: KioskLayoutWidget[] | undefined;
  if (!('layout' in value)) {
    issue(issues, 'layout', 'MISSING_FIELD', 'layout is required');
  } else if (!Array.isArray(value.layout)) {
    issue(issues, 'layout', 'INVALID_TYPE', 'layout must be an array');
  } else if (value.layout.length > GEOMETRY.maxWidgets) {
    issue(issues, 'layout', 'TOO_MANY_WIDGETS', `layout exceeds ${GEOMETRY.maxWidgets} widgets`);
  } else {
    const widgets: KioskLayoutWidget[] = [];
    const seenIds = new Set<string>();
    let layoutValid = true;
    value.layout.forEach((entry, index) => {
      const widget = parseWidget(issues, entry, `layout[${index}]`);
      if (!widget) {
        layoutValid = false;
        return;
      }
      if (seenIds.has(widget.widgetId)) {
        issue(
          issues,
          `layout[${index}].widgetId`,
          'DUPLICATE_WIDGET_ID',
          `widgetId "${widget.widgetId}" is used more than once`,
        );
        layoutValid = false;
        return;
      }
      seenIds.add(widget.widgetId);
      widgets.push(widget);
    });
    if (layoutValid) layout = widgets;
  }

  let visibility: KioskTemplateVisibility | undefined;
  if (!('visibility' in value)) {
    issue(issues, 'visibility', 'MISSING_FIELD', 'visibility is required');
  } else {
    visibility = parseVisibility(issues, value.visibility);
  }

  // Fail closed on ANY collected issue — strict envelope, no partial acceptance.
  if (
    issues.length > 0 ||
    schemaVersion === undefined ||
    templateId === undefined ||
    version === undefined ||
    layout === undefined ||
    visibility === undefined
  ) {
    return failParse(issues);
  }

  return okParse({ schemaVersion, templateId, version, layout, visibility });
}

/** Throwing variant of {@link parseKioskRenderDocument} for fail-closed boot paths. */
export function assertKioskRenderDocument(value: unknown): KioskRenderDocument {
  const outcome = parseKioskRenderDocument(value);
  if (!outcome.ok) {
    throw new KioskRendererError(outcome.issues);
  }
  return outcome.value;
}
